'use client';

import {ReactNode, useRef} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import type {AnimationType, StackEntry} from '@/app/(app)/games/[gid]/_src/SkillzSDK/types';
import {useSkillzStyle} from '@/app/(app)/games/[gid]/_src/SkillzStyleProvider';

interface AnimatedStackProps {
  stack: StackEntry[];
  children: ReactNode;
  animationType?: AnimationType;
  duration?: number;
}

export const AnimatedStack = ({
  stack,
  children,
  animationType = 'slide',
  duration = 350,
}: AnimatedStackProps) => {
  const prevStackLength = useRef(1);
  const stackWithChildren = [{component: children, scene: 'children-entry'}, ...stack];
  const currentEntry = stackWithChildren[stackWithChildren.length - 1];
  const key = currentEntry?.id || currentEntry?.scene || `stack-${stackWithChildren.length}`;
  const {themeJSON, themeImagesUrl} = useSkillzStyle();
  const image = themeJSON?.images?.backgroundImage;
  const backgroundGradient = themeJSON?.gradients?.backgroundViewGradient;
  const backgroundStyle = {
    backgroundImage: 'none',
  };
  if (themeImagesUrl && image) {
    backgroundStyle.backgroundImage = `url(${themeImagesUrl}${image})`;
  } else if (backgroundGradient) {
    backgroundStyle.backgroundImage = `linear-gradient(180deg, ${backgroundGradient[0]}, ${backgroundGradient[1]})`;
  }
  const variants = {
    initial: (direction: 'forward' | 'backward') => ({
      x: animationType === 'slide' ? (direction === 'forward' ? '-100%' : '100%') : 0,
      opacity: animationType === 'fade' ? 0 : 1,
      width: '100%',
    }),
    animate: {
      x: 0,
      opacity: 1,
      width: '100%',
      transition: {duration: duration / 1000},
    },
    exit: (direction: 'forward' | 'backward') => ({
      x: animationType === 'slide' ? (direction === 'forward' ? '100%' : '-100%') : 0,
      opacity: animationType === 'fade' ? 0 : 1,
      width: '100%',
      transition: {duration: duration / 1000},
    }),
  };

  const direction: 'forward' | 'backward' =
    stackWithChildren.length === 1 || stackWithChildren.length < prevStackLength.current
      ? 'forward'
      : 'backward';

  prevStackLength.current = stackWithChildren.length;

  return (
    <div id='animated-stack' className='relative w-full h-full overflow-hidden'>
      <AnimatePresence initial={false} custom={direction} mode='popLayout'>
        <motion.div
          key={key}
          custom={direction}
          variants={variants}
          initial='initial'
          animate='animate'
          exit='exit'
          style={backgroundStyle}
          className='w-full h-full flex'
          layout
        >
          {currentEntry.component}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
